/**
 * Detect whether a note/summary value is Lexical-editor HTML (vs markdown or
 * plain text). Lives in `lib/` because both the client render dispatcher
 * (`NotesContentViewer`) and server-side code (intake normalization, the
 * notes→markdown backfill) must agree on the same classification.
 *
 * Detection keys off an opening tag whose name is one the Lexical editor roots
 * its serialized content in — not a bare leading `<`, not any tag-shaped token,
 * and not inline tags. The editor (see `RichTextEditor`) registers only
 * paragraph, list, and text nodes, so `$generateHtmlFromNodes` always opens a
 * note with `<p>`, `<ul>`, or `<ol>`; inline formatting (`<strong>`, `<a>`,
 * `<span>`, …) only ever appears nested inside those blocks.
 *
 * Everything else stays on the markdown path. A markdown note can legitimately
 * begin with `<`: a CommonMark autolink (`<https://example.com>`), a `<3`, a
 * literal `<`, an angle-bracketed label (`<Alice> …`), or even a leading inline
 * raw-HTML tag (`<a href="…">site</a> and **bold**`) — markdown handles raw HTML
 * via `rehypeRaw` while still rendering the surrounding markdown. Routing those
 * to the HTML-only pipeline would make the trailing markdown render literally.
 * The tag name is matched case-sensitively: the editor always emits lowercase.
 */
const EDITOR_ROOT_HTML_TAGS = new Set(["p", "ul", "ol"]);
const HTML_OPENING_TAG = /^\s*<([a-zA-Z][a-zA-Z0-9-]*)(\s[^>]*)?\/?>/;

export function isHtmlContent(value: string): boolean {
  const tag = HTML_OPENING_TAG.exec(value)?.[1];
  return tag != null && EDITOR_ROOT_HTML_TAGS.has(tag);
}
